import { memo } from 'react';
import { motion } from 'framer-motion';
import { ludokaLogo } from '@/assets';
import { PillButton } from '@/shared/components/PillButton';

export interface ErrorPageProps {
  title: string;
  message: string;
  fixingMessage: string;
  reloadLabel: string;
  onReload: () => void;
}

export const ErrorPage = memo(function ErrorPage({
  title,
  message,
  fixingMessage,
  reloadLabel,
  onReload
}: ErrorPageProps) {
  return (
    <motion.div
      className="absolute inset-0 z-[100] flex flex-col items-center justify-center bg-gradient-to-b from-[#0A2E1B] to-[#145C32] p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Logo */}
      <motion.img
        src={ludokaLogo}
        alt="LUDOKA"
        className="w-48 h-auto mb-8"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      />

      {/* Warning Icon - Red card feel */}
      <motion.div
        className="mb-6"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.15, type: 'spring', stiffness: 200 }}
      >
        <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9V13M12 17H12.01"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </motion.div>

      {/* Title */}
      <motion.h1
        className="font-mind-boggle text-4xl text-white text-center mb-4"
        style={{
          textShadow:
            '3px 3px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 2px 2px 6px rgba(0,0,0,0.5)'
        }}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        {title}
      </motion.h1>

      {/* Message */}
      <motion.p
        className="font-mind-boggle text-lg text-white/90 text-center max-w-xs leading-relaxed"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {message}
      </motion.p>

      {/* Fixing message */}
      <motion.p
        className="font-mind-boggle text-base text-yellow-400 text-center mt-3 mb-8"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        {fixingMessage}
      </motion.p>

      {/* Reload button */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.5 }}
      >
        <PillButton variant="blue" className="w-56 h-14" onClick={onReload}>
          {reloadLabel}
        </PillButton>
      </motion.div>
    </motion.div>
  );
});

export default ErrorPage;
